"use client";
import type React from "react";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { categoryAPI, type Collection, type Product } from "../services/api";

import { VideoPlayer } from "../components/ui/VideoPlayer";
import placeholderImage from "../assets/placeholder-image.png";
import hammerimage from "../assets/hammer.png"
import uvimage from "../assets/uv.png"
import nottoxicimage from "../assets/nottoxic.png"
import windimg from "../assets/wind.png"
import { Play } from "lucide-react";
import { FabricImageZoomPan } from "../components/ui/FabricImageZoomPan";
import { SeoHelmet } from "../components/SeoHelmet";
import LogoLoader from "../components/ui/LogoLoader";
import { DEFAULT_DESCRIPTION } from "../config/seo";
import { productThumbUrl, videoPosterUrl } from "../utils/mediaUrls";

const CategoryPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [category, setCategory] = useState<Collection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedFabric, setSelectedFabric] = useState<Product | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      try {
        const categoryResponse = await categoryAPI.getById(Number.parseInt(id));
        const data: Collection = categoryResponse.data;
        setCategory(data);

        // Находим "featured" продукт
        const featuredFabric = data.products.find(
          (fabric) => fabric.id === data.featuredProductId
        );

        if (featuredFabric) {
          setSelectedFabric(featuredFabric);
          if (featuredFabric.videos.length > 0) {
            setSelectedVideo(process.env.REACT_APP_API_URL + featuredFabric.videos[0]);
            setSelectedImage(null);
          } else if (featuredFabric.images.length > 0) {
            setSelectedVideo(null);
            setSelectedImage(process.env.REACT_APP_API_URL + featuredFabric.images[0]);
          }
        }

        setLoading(false);
      } catch {
        setError("Ошибка загрузки данных");
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  function sortProductsByNameNumber(products: Product[]) {
    return [...products].sort((a, b) => {
      const numA = Number.parseInt(a.name.replace(/\D+/g, ""), 10) || 0;
      const numB = Number.parseInt(b.name.replace(/\D+/g, ""), 10) || 0;
      return numA - numB;
    });
  }

  if (loading) {
    return (
      <>
        <SeoHelmet
          title="Категория"
          description={DEFAULT_DESCRIPTION}
          path={id ? `/category/${id}` : "/catalog"}
        />
        <div className="category-page">
          <div className="container">
            <LogoLoader />
          </div>
        </div>
      </>
    );
  }

  if (error || !category) {
    return (
      <>
        <SeoHelmet
          title="Категория"
          description={DEFAULT_DESCRIPTION}
          path={id ? `/category/${id}` : "/catalog"}
        />
        <div className="category-page">
          <div className="container">
            <div className="error-state">{error || "Категория не найдена"}</div>
          </div>
        </div>
      </>
    );
  }

  const catDesc =
    category.description?.trim().slice(0, 300) ||
    `Коллекция «${category.name}» — каталог тканей ТЕКС МОЛЛ.`;

  return (
    <div className="category-page">
      <SeoHelmet title={category.name} description={catDesc} path={`/category/${id}`} />
      {/* Hero */}
      <section className="category-hero reveal">
        <div>
          <nav className="breadcrumb">
            <Link to="/catalog">Каталог</Link>
            <span className="breadcrumb-separator">&rarr;</span>
            <span>{category.name}</span>
            <span className="breadcrumb-separator">&rarr;</span>
            <span>{selectedFabric?.name || ''}</span>
          </nav>
        </div>
      </section>

      {/* Main Content */}
      <div className="category-page--split">
        {/* Left: Видео или Изображение */}
        <div className="category-page__left reveal reveal-delay-1">
          <div className="main-image-wrapper">
            {selectedImage ? (
              <FabricImageZoomPan
                src={selectedImage}
                alt={selectedFabric?.name || "Выбранная ткань"}
                className="category-page__main-zoom"
              />
            ) : selectedVideo ? (
              <VideoPlayer src={selectedVideo} />
            ) : (
              <div className="nocontent">Цвета коллекции пока не загружены</div>
            )}
          </div>

          {/* Превью изображений и видео выбранной ткани */}
          {selectedFabric && (selectedFabric.images.length > 0 || selectedFabric.videos.length > 0) && (
            <div className="fabrics-preview-grid">
              {selectedFabric.images.map((img, idx) => {
                const fullUrl = process.env.REACT_APP_API_URL + img;
                const thumbUrl = productThumbUrl(fullUrl);
                return (
                  <div
                    key={`${selectedFabric.id}-img-${idx}`}
                    className={`fabric-preview-card${selectedImage === fullUrl ? " selected" : ""
                      }`}
                    onClick={() => {
                      setSelectedImage(fullUrl);
                      setSelectedVideo(null);
                    }}
                    title={`${selectedFabric.name} — изображение ${idx + 1}`}
                  >
                    <img
                      src={thumbUrl}
                      alt={selectedFabric.name}
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        if (e.currentTarget.src !== fullUrl) {
                          e.currentTarget.src = fullUrl;
                          return;
                        }
                        e.currentTarget.src = placeholderImage;
                      }}
                    />
                    <span className="fabric-preview-number"></span>
                  </div>
                );
              })}
              {selectedFabric.videos.map((video, idx) => {
                const videoUrl = process.env.REACT_APP_API_URL + video;
                const poster = videoPosterUrl(videoUrl);
                const imageFallback = selectedFabric.images[0]
                  ? productThumbUrl(process.env.REACT_APP_API_URL + selectedFabric.images[0])
                  : placeholderImage;
                return (
                  <div
                    key={`${selectedFabric.id}-sample-video-${idx}`}
                    className={`fabric-preview-card${selectedVideo === videoUrl && !selectedImage ? " selected" : ""}`}
                    onClick={() => {
                      setSelectedFabric(selectedFabric);
                      setSelectedVideo(videoUrl);
                      setSelectedImage(null);
                    }}
                    title={`${selectedFabric.name} — видео`}
                  >
                    <img
                      src={poster}
                      alt={`${selectedFabric.name} — видео`}
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        if (e.currentTarget.src !== imageFallback) {
                          e.currentTarget.src = imageFallback;
                        }
                      }}
                    />
                    <Play className="fabric-preview-videoicon" />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: превью + информация */}
        <div className="category-page__right">
          <div className="reveal reveal-delay-1">
            <h1 className="category-title">{category.name}</h1>
            <p className="category-subtitle">Цвета коллекции</p>
          </div>

          {/* Сетка превью видео всех тканей */}
          <div className="catalog-preview-grid reveal reveal-delay-2">
            {sortProductsByNameNumber(category.products).map((fabric) => {
              const colorNumber = parseInt(fabric.name.replace(/\D+/g, ""), 10);
              if (fabric.videos.length > 0) {
                return fabric.videos.map((video, idx) => {
                  const videoUrl = process.env.REACT_APP_API_URL + video;
                  const previewFull =
                    fabric.images.length > 0
                      ? process.env.REACT_APP_API_URL + fabric.images[0]
                      : "";
                  const previewImg = previewFull
                    ? productThumbUrl(previewFull)
                    : placeholderImage;

                  return (
                    <div
                      key={`${fabric.id}-video-${idx}`}
                      className={`fabric-preview-card${selectedVideo === videoUrl ? " selected" : ""
                        }`}
                      onClick={() => {
                        setSelectedFabric(fabric);
                        setSelectedVideo(videoUrl);
                        setSelectedImage(null);
                      }}
                      title={`${fabric.name} — видео`}
                    >
                      <img
                        src={previewImg}
                        alt={`${fabric.name} видео превью`}
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          if (previewFull && e.currentTarget.src !== previewFull) {
                            e.currentTarget.src = previewFull;
                            return;
                          }
                          e.currentTarget.src = placeholderImage;
                        }}
                      />
                      <Play className="fabric-preview-videoicon" />
                      <span className="fabric-preview-video">
                        {colorNumber}
                      </span>
                    </div>
                  );
                });
              }

              if (fabric.images.length === 0) return null;
              const fullUrl = process.env.REACT_APP_API_URL + fabric.images[0];
              const previewImg = productThumbUrl(fullUrl);
              return (
                <div
                  key={`${fabric.id}-color`}
                  className={`fabric-preview-card${selectedImage === fullUrl ? " selected" : ""}`}
                  onClick={() => {
                    setSelectedFabric(fabric);
                    setSelectedImage(fullUrl);
                    setSelectedVideo(null);
                  }}
                  title={fabric.name}
                >
                  <img
                    src={previewImg}
                    alt={fabric.name}
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      if (e.currentTarget.src !== fullUrl) {
                        e.currentTarget.src = fullUrl;
                        return;
                      }
                      e.currentTarget.src = placeholderImage;
                    }}
                  />
                  <span className="fabric-preview-video">{colorNumber}</span>
                </div>
              );
            })}
          </div>

          {/* Информация о ткани */}

          <div className="category-page__tech reveal reveal-delay-3">
            <h4>Технические данные</h4>
            <div className="tech-wrapper">
              <div className="tech-content">
                {selectedFabric ? (
                  <>
                    <div>
                      <p>Тип ткани:
                      </p>
                      <p>{selectedFabric.type}
                      </p>
                    </div>
                    <div>
                      <p>
                        Устойчивость к истиранию:
                      </p>
                      <p>
                        {selectedFabric.resistance} циклов
                      </p>
                    </div>
                    <div>
                      <p>Плотность:
                      </p>
                      <p>{selectedFabric.density} г/м2
                      </p>
                    </div>
                    <div>
                      <p>Ширина:
                      </p>
                      <p>{selectedFabric.width} +/-2см
                      </p>
                    </div>
                  </>
                ) : (
                  <div>Выберите ткань, чтобы увидеть детали</div>
                )}
              </div>
              <div className="techIcons">
                <div className="tech-icon">
                  <img src={hammerimage} alt="" />
                  <span>{"Износо\u00ADустойчивый"}</span>
                </div>
                <div className="tech-icon">
                  <img src={uvimage} alt="" />
                  <span>{"Цвето\u00ADустойчивый"}</span>
                </div>
                <div className="tech-icon">
                  <img src={nottoxicimage} alt="" />
                  <span>Нетоксичный</span>
                </div>
                <div className="tech-icon">
                  <img src={windimg} alt="" />
                  <span>Дышащая ткань</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;
